import { useState } from 'react';
import { Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { IconCheckAll } from '@/components/icons';
import { useMarkNotificationsRead, useNotifications } from '@/data/hooks';
import { NotificationItem } from './NotificationItem';

/**
 * Full Notificaciones page. A "Todas / Sin leer" filter + a "Marcar todas como
 * leídas" action, with per-item mark-read. Notifications are always the caller's
 * own (scoped server-side). Mobile-first list with consistent loading/empty/error
 * states via QueryState.
 */
export function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const query = useNotifications({ unread: unreadOnly || undefined, limit: 100 });
  const markRead = useMarkNotificationsRead();

  const items = query.data?.items ?? [];
  const unreadCount = query.data?.unreadCount ?? 0;

  const tabs: { label: string; value: boolean }[] = [
    { label: 'Todas', value: false },
    { label: `Sin leer${unreadCount > 0 ? ` (${unreadCount})` : ''}`, value: true },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Notificaciones"
        description="Avisos de pedidos, pagos, facturas y más."
        actions={
          unreadCount > 0 ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => markRead.mutate({ all: true })}
              disabled={markRead.isPending}
            >
              <IconCheckAll className="h-4 w-4" />
              {markRead.isPending ? 'Marcando…' : 'Marcar todas como leídas'}
            </Button>
          ) : undefined
        }
      />

      {/* Filter tabs. */}
      <div className="scrollbar-hover -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {tabs.map((tab) => {
          const active = tab.value === unreadOnly;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setUnreadOnly(tab.value)}
              aria-pressed={active}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-brand-primary bg-brand-primary/10 font-medium text-brand-primary'
                  : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingLabel="Cargando notificaciones…"
          emptyTitle={unreadOnly ? 'Sin notificaciones nuevas' : 'No tienes notificaciones'}
          emptyDescription={
            unreadOnly
              ? 'Estás al día. No hay notificaciones sin leer.'
              : 'Aquí aparecerán los avisos de tu cuenta.'
          }
        >
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <li key={n.notificationId}>
                <NotificationItem
                  notification={n}
                  onMarkRead={(id) => markRead.mutate({ notificationIds: [id] })}
                />
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}
